import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../App.css";
import bgimg from "../images/Hero-image-V1.png";
import transdyneLogo from "../images/transdyne-01.svg";
import { loginRequest, msalReady, pca, tokenRequest } from "../msalConfig";

export const Login = () => {
  const [token, setToken] = useState(null);
  const [interactionInProgress, setInteractionInProgress] = useState(false);
  const interactionLock = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    const getToken = async () => {
      try {
        const redirectResponse = await msalReady;
        if (redirectResponse?.account) {
          pca.setActiveAccount(redirectResponse.account);
        }

        const account = pca.getActiveAccount() || pca.getAllAccounts()[0];
        if (!account) {
          if (mounted) setToken(null);
          return;
        }

        pca.setActiveAccount(account);
        const tokenResponse = await pca.acquireTokenSilent({
          ...tokenRequest,
          account,
        });
        if (mounted) {
          setToken(tokenResponse.accessToken);
          navigate("/home", { replace: true });
        }
      } catch (error) {
        if (mounted) setToken(null);
        // Silent token acquisition can fail when the user needs to sign in again.
        // Leave the sign-in button available rather than starting a popup automatically.
        console.error("Unable to acquire an access token silently:", error);
      }
    };

    getToken();
    return () => {
      mounted = false;
    };
  }, [navigate]);

  const handleSignIn = async () => {
    // A ref blocks rapid repeated clicks before React has rendered the disabled state.
    if (interactionLock.current) return;
    interactionLock.current = true;
    setInteractionInProgress(true);

    try {
      await msalReady;
      const loginResponse = await pca.loginPopup(loginRequest);
      if (loginResponse.account) {
        pca.setActiveAccount(loginResponse.account);
        // The popup completed successfully; let the app acquire API tokens silently
        // where needed instead of starting another interactive authentication flow.
        setToken(loginResponse.accessToken || "signed-in");
        navigate("/home", { replace: true });
      }
    } catch (error) {
      // In particular, don't start a second popup if MSAL reports an active interaction.
      console.error("Sign-in failed:", error);
    } finally {
      interactionLock.current = false;
      setInteractionInProgress(false);
    }
  };

  const handleSignOut = async () => {
    if (interactionLock.current) return;
    interactionLock.current = true;
    setInteractionInProgress(true);
    try {
      await msalReady;
      await pca.logoutPopup();
      setToken(null);
    } catch (error) {
      console.error("Sign-out failed:", error);
    } finally {
      interactionLock.current = false;
      setInteractionInProgress(false);
    }
  };

  return (
    <>
      {!token && (
        <div
          className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-400 to-purple-500"
          style={{
            backgroundImage: `url(${bgimg})`,
            backgroundSize: "55%",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center left",
            backgroundColor: "#f0f4f8",
          }}
        >
          <div className="container ml-auto">
            <div className="flex flex-col md:flex-row">
              <div className="md:w-1/2 h-screen bg-cover bg-center md:block hidden"></div>
              <div className="md:w-1/2 bg-white p-8 shadow-lg flex flex-col items-center justify-center relative">
                <img
                  src={transdyneLogo}
                  alt="Icon"
                  className="mb-[100px] w-50% h-8"
                />
                <div className="text-center mb-10">
                  <h3 className="text-3xl font-semibold mb-4">Welcome back!</h3>
                </div>
                <div className={"buttonContainer"}>
                  <input
                    className={"inputButton"}
                    type="button"
                    onClick={token ? handleSignOut : handleSignIn}
                    disabled={interactionInProgress}
                    value={
                      interactionInProgress
                        ? "Please wait..."
                        : token
                          ? "Log out"
                          : "Log in"
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
